import { ref, computed, watch } from "vue"
import { useI18n } from "vue-i18n"
import type { Address } from "@types"
import {
    useUserSearch,
    type UserSuggestion,
    useCompanyForm,
    type CompanyType,
} from "@core/composables"
import { useAuthStore } from "@core/stores/auth.store"

// =====================
// Types
// =====================

/**
 * Type de titulaire
 */
export type OwnerType = "self" | "client"

/**
 * Type d'utilisateur (particulier ou professionnel)
 */
export type UserType = "particular" | "professional"

/**
 * Données utilisateur
 */
export type OwnerUserData = {
    /**
     * Identifiant MongoDB (si utilisateur existant)
     */
    _id?: string
    /**
     * Prénom
     */
    firstname: string
    /**
     * Nom
     */
    lastname: string
    /**
     * Email (username)
     */
    username: string
}

/**
 * Données entreprise
 */
export type OwnerCompanyData = {
    /**
     * Identifiant MongoDB (si entreprise existante)
     */
    _id?: string
    /**
     * Nom de l'organisation
     */
    name: string
    /**
     * Adresse de l'organisation
     */
    address: Address | null
    /**
     * Type d'organisation
     */
    type: CompanyType | ""
    /**
     * Identifiant de l'organisation (SIREN, RNA, n° copro)
     */
    idcompany?: string
    /**
     * Forme juridique (pour les entreprises)
     */
    legalForm?: string
}

/**
 * Output unifié du module Owner
 */
export type OwnerOutput = {
    /**
     * Données utilisateur
     */
    user: OwnerUserData
    /**
     * Données entreprise (null si particulier)
     */
    company: OwnerCompanyData | null
    /**
     * True lorsque le titulaire est l'utilisateur connecté ("Mon entreprise")
     */
    isConnectedUser?: boolean
}

/**
 * Entreprise existante de l'utilisateur trouvé
 */
export type ExistingCompany = {
    _id: string
    name: string
    idcompany?: string
    legalForm?: string
    type?: string
    address?: Address
}

/**
 * Utilisateur trouvé par la recherche
 */
export type FoundUser = {
    _id?: string
    firstname?: string
    lastname?: string
    username?: string
    multiCompany?: ExistingCompany[]
}

/**
 * Options du composable
 */
export type UseOwnerFormOptions = {
    /**
     * Valeur initiale
     */
    initialValue?: Partial<OwnerOutput>
    /**
     * Type d'utilisateur accepté (undefined = les deux types acceptés)
     */
    allowedUserType?: UserType
}

// =====================
// Composable
// =====================

/**
 * Composable centralisé pour gérer le formulaire de titulaire
 * @param options - Options de configuration
 * @returns État, validation et actions du formulaire
 */
export function useOwnerForm(options: UseOwnerFormOptions = {}) {
    const { t } = useI18n({
        useScope: "global",
        messages: {
            fr: {
                ownerForm: {
                    ownerType: {
                        self: "Mon entreprise",
                        client: "Client externe",
                    },
                    userType: {
                        particular: "Particulier",
                        professional: "Professionnel",
                    },
                    validation: {
                        firstname: "Le prénom est obligatoire",
                        lastname: "Le nom est obligatoire",
                        email: "L'email est obligatoire",
                        emailInvalid: "L'email n'est pas valide",
                    },
                },
            },
        },
    })

    const authStore = useAuthStore()

    // =====================
    // Composables internes
    // =====================

    const {
        suggestions,
        isSearching: isSearchingSuggestions,
        searchUserByEmail,
    } = useUserSearch({
        types: ["client"],
        fetchOnMount: true,
    })

    const {
        form: companyForm,
        companyTypeOptions,
        legalFormOptions,
        companyIdErrorMessage,
        isValidCompany,
        onTypeChange: onCompanyTypeChange,
        resetForm: resetCompanyForm,
        setFormData: setCompanyFormData,
    } = useCompanyForm()

    // =====================
    // État
    // =====================

    /**
     * Type de titulaire (mon entreprise ou client externe)
     */
    const ownerType = ref<OwnerType>("client")

    /**
     * Données utilisateur
     */
    const user = ref<OwnerUserData>({
        firstname: "",
        lastname: "",
        username: "",
    })

    /**
     * Type d'utilisateur (particulier ou professionnel)
     * Initialisé selon allowedUserType si défini, sinon "particular" par défaut
     */
    const userType = ref<UserType>(options.allowedUserType || "particular")

    /**
     * Entreprise sélectionnée (parmi les existantes)
     */
    const selectedCompany = ref<ExistingCompany | null>(null)

    /**
     * Utilisateur trouvé par la recherche
     */
    const foundUser = ref<FoundUser | null>(null)

    /**
     * Indique si la recherche utilisateur a été effectuée
     */
    const hasSearchedUser = ref(false)

    /**
     * Indique si la recherche par email est en cours
     */
    const isSearchingByEmail = ref(false)

    /**
     * Suggestions filtrées pour l'autocomplétion
     */
    const filteredSuggestions = ref<UserSuggestion[]>([])

    // =====================
    // Données calculées
    // =====================

    /**
     * Options pour le type de titulaire
     */
    const getOwnerTypeOptions = computed(() => [
        { label: t("ownerForm.ownerType.self"), value: "self" },
        { label: t("ownerForm.ownerType.client"), value: "client" },
    ])

    /**
     * Options pour le type d'utilisateur
     */
    const getUserTypeOptions = computed<
        Array<{ label: string; value: UserType }>
    >(() => [
        { label: t("ownerForm.userType.particular"), value: "particular" },
        { label: t("ownerForm.userType.professional"), value: "professional" },
    ])

    /**
     * Indique si l'utilisateur trouvé a des entreprises
     */
    const getUserHasCompanies = computed(() => {
        return (foundUser.value?.multiCompany?.length ?? 0) > 0
    })

    /**
     * Liste des entreprises de l'utilisateur trouvé
     */
    const getUserCompanies = computed<ExistingCompany[]>(() => {
        return foundUser.value?.multiCompany ?? []
    })

    /**
     * Vérifie si les champs utilisateur de base sont remplis
     */
    const getIsUserBasicValid = computed(() => {
        return (
            !!user.value.firstname &&
            !!user.value.lastname &&
            !!user.value.username &&
            isValidEmail(user.value.username)
        )
    })

    /**
     * Vérifie si le formulaire utilisateur est valide
     */
    const getIsUserValid = computed(() => {
        return getIsUserBasicValid.value
    })

    /**
     * Vérifie si le formulaire entreprise est valide
     */
    const getIsCompanyValid = computed(() => {
        // Si on a sélectionné une entreprise existante
        if (selectedCompany.value) {
            return true
        }
        // Sinon, valider le formulaire de création
        return isValidCompany.value
    })

    /**
     * Vérifie si le formulaire complet est valide
     */
    const getIsValid = computed(() => {
        // "Mon entreprise" : valide dès que l'utilisateur connecté est chargé
        if (ownerType.value === "self") {
            return !!(
                user.value.firstname &&
                user.value.lastname &&
                user.value.username
            )
        }

        // Le formulaire n'est valide que si la recherche a été effectuée
        // On doit être sûr de savoir si c'est un particulier ou un professionnel
        if (!hasSearchedUser.value) {
            return false
        }

        // Si particulier, seul l'utilisateur est requis
        if (userType.value === "particular") {
            return getIsUserValid.value
        }

        // Si professionnel, utilisateur + entreprise requis
        return getIsUserValid.value && getIsCompanyValid.value
    })

    /**
     * Indique si on doit afficher le formulaire entreprise
     */
    const getShowCompanyForm = computed(() => {
        if (!hasSearchedUser.value) return false

        // Si un type est imposé mais que l'utilisateur trouvée n'a pas d'entreprises, ne pas afficher le formulaire
        if (
            // Si un type est imposé
            options.allowedUserType &&
            // Si l'utilisateur trouvé existe
            !!foundUser.value &&
            // Si l'utilisateur trouvé n'a pas d'entreprises
            !getUserHasCompanies.value
        )
            return false

        // Si l'utilisateur trouvé a des entreprises, afficher le formulaire
        if (getUserHasCompanies.value) return true

        // Sinon, afficher si professionnel
        return userType.value === "professional"
    })

    /**
     * Indique si on doit afficher le sélecteur de type utilisateur (Particulier / Professionnel).
     * Affiché uniquement quand l'utilisateur n'a pas été trouvé (nouvel utilisateur) : dans ce cas
     * on ne connaît pas encore son type. Si un utilisateur a été trouvé (particulier ou pro),
     * on n'affiche pas le sélecteur car le type est déjà connu via la recherche.
     */
    const getShowUserTypeSelector = computed(() => {
        if (!hasSearchedUser.value) return false
        if (foundUser.value) return false
        return true
    })

    /**
     * Options de type utilisateur disponibles (filtrées selon allowedUserType)
     */
    const getAllowedUserTypeOptions = computed(() => {
        if (options.allowedUserType) {
            // Si un type est imposé, ne retourner que ce type
            return getUserTypeOptions.value.filter(
                (option) => option.value === options.allowedUserType
            )
        }
        // Sinon, retourner toutes les options
        return getUserTypeOptions.value
    })

    /**
     * Indique si le type d'utilisateur trouvé ne correspond pas au type attendu
     */
    const getUserTypeMismatch = computed(() => {
        if (
            !options.allowedUserType ||
            !hasSearchedUser.value ||
            !foundUser.value
        ) {
            return false
        }

        const foundUserType = getUserHasCompanies.value
            ? "professional"
            : "particular"
        return foundUserType !== options.allowedUserType
    })

    /**
     * Output unifié du formulaire
     */
    const getOutput = computed<OwnerOutput>(() => {
        const userData: OwnerUserData = foundUser.value
            ? {
                  _id: foundUser.value._id,
                  firstname: foundUser.value.firstname || user.value.firstname,
                  lastname: foundUser.value.lastname || user.value.lastname,
                  username: foundUser.value.username || user.value.username,
              }
            : {
                  firstname: user.value.firstname,
                  lastname: user.value.lastname,
                  username: user.value.username,
              }

        const withConnectedFlag = (out: OwnerOutput) =>
            ownerType.value === "self"
                ? { ...out, isConnectedUser: true as const }
                : out

        // Si particulier, pas d'entreprise
        if (userType.value === "particular") {
            return withConnectedFlag({
                user: userData,
                company: null,
            })
        }

        // Si entreprise sélectionnée
        if (selectedCompany.value) {
            return withConnectedFlag({
                user: userData,
                company: {
                    _id: selectedCompany.value._id,
                    name: selectedCompany.value.name,
                    address: selectedCompany.value.address || null,
                    type: (selectedCompany.value.type as CompanyType) || "",
                    idcompany: selectedCompany.value.idcompany,
                    legalForm: selectedCompany.value.legalForm,
                },
            })
        }

        // Sinon, nouvelle entreprise
        const companyData = {
            name: companyForm.value.name,
            address: companyForm.value.address,
            type: companyForm.value.type,
            idcompany: companyForm.value.idcompany,
            legalForm: companyForm.value.legalForm,
        } as OwnerCompanyData
        if (companyForm.value._id) {
            companyData._id = companyForm.value._id
        }
        return withConnectedFlag({
            user: userData,
            company: companyData,
        })
    })

    // =====================
    // Validation
    // =====================

    /**
     * Vérifie si un email est valide
     */
    function isValidEmail(email: string): boolean {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
        return emailRegex.test(email)
    }

    // =====================
    // Actions
    // =====================

    /**
     * Recherche un utilisateur par email
     */
    async function searchUser(): Promise<void> {
        if (!getIsUserBasicValid.value) return

        isSearchingByEmail.value = true

        try {
            const result = await searchUserByEmail(user.value.username)

            if (result) {
                foundUser.value = result
                // Si l'utilisateur a des entreprises, passer en mode pro
                if (result.multiCompany && result.multiCompany.length > 0) {
                    userType.value = "professional"
                    // Sélectionner la première entreprise par défaut
                    selectedCompany.value = result.multiCompany[0]
                } else {
                    // Si aucun type n'est imposé, laisser le type par défaut
                    // Sinon, utiliser le type imposé
                    if (options.allowedUserType) {
                        userType.value = options.allowedUserType
                    }
                }
            } else {
                foundUser.value = null
                // Si aucun utilisateur trouvé et qu'un type est imposé, le pré-sélectionner
                if (options.allowedUserType) {
                    userType.value = options.allowedUserType
                }
            }

            hasSearchedUser.value = true
        } catch (error: any) {
            if (error?.response?.data?.detail?.code !== "not_found_user") {
                hasSearchedUser.value = false
            } else {
                // Utilisateur non trouvé : on marque la recherche comme effectuée
                // et on réinitialise foundUser pour permettre la création d'un nouvel utilisateur
                foundUser.value = null
                hasSearchedUser.value = true
            }
        } finally {
            isSearchingByEmail.value = false
        }
    }

    /**
     * Définit le titulaire comme l'utilisateur connecté (données du store auth)
     */
    function setOwnerFromCurrentUser(): void {
        user.value = {
            _id: authStore.userId,
            firstname: authStore.firstname,
            lastname: authStore.lastname,
            username: authStore.username,
        }

        foundUser.value = {
            _id: authStore.userId,
            firstname: authStore.firstname,
            lastname: authStore.lastname,
            username: authStore.username,
        }

        if (authStore.companyId && authStore.companyName) {
            userType.value = "professional"
            selectedCompany.value = {
                _id: authStore.companyId,
                name: authStore.companyName,
                idcompany: authStore.companyIdcompany,
                legalForm: authStore.companyLegalForm,
                address: authStore.companyAddress,
                type: authStore.companyType,
            }
        }

        hasSearchedUser.value = true
    }

    /**
     * Sélectionne une entreprise existante
     */
    function selectCompany(company: ExistingCompany | null): void {
        selectedCompany.value = company
    }

    /**
     * Filtre les suggestions d'autocomplétion
     */
    function filterSuggestions(query: string): void {
        const q = query.toLowerCase().trim()

        if (!q) {
            filteredSuggestions.value = [...suggestions.value]
            return
        }

        filteredSuggestions.value = suggestions.value.filter(
            (suggestion) =>
                suggestion.name.toLowerCase().includes(q) ||
                suggestion.user.firstname?.toLowerCase().includes(q) ||
                suggestion.user.lastname?.toLowerCase().includes(q)
        )
    }

    /**
     * Gère la sélection d'un utilisateur dans l'autocomplétion
     */
    async function onSelectSuggestion(
        suggestion: UserSuggestion
    ): Promise<void> {
        // Réinitialiser l'état avant de remplir les nouveaux champs
        resetSearchState()

        // Remplir les champs avec les données de l'utilisateur sélectionné
        user.value = {
            firstname: suggestion.user.firstname || "",
            lastname: suggestion.user.lastname || "",
            username: suggestion.user.username || "",
        }

        // Lancer automatiquement la recherche
        await searchUser()
    }

    /**
     * Réinitialise l'état de recherche
     */
    function resetSearchState(): void {
        hasSearchedUser.value = false
        foundUser.value = null
        selectedCompany.value = null
        // Réinitialiser au type imposé si défini, sinon "particular"
        userType.value = options.allowedUserType || "particular"
        resetCompanyForm()
    }

    /**
     * Réinitialise l'état du formulaire sans modifier le type de titulaire
     */
    function resetFormState(): void {
        user.value = {
            firstname: "",
            lastname: "",
            username: "",
        }
        // Réinitialiser au type imposé si défini, sinon "particular"
        userType.value = options.allowedUserType || "particular"
        selectedCompany.value = null
        foundUser.value = null
        hasSearchedUser.value = false
        filteredSuggestions.value = []
        resetCompanyForm()
    }

    /**
     * Réinitialise complètement le formulaire (type client + état vide)
     */
    function reset(): void {
        resetFormState()
        ownerType.value = "client"
    }

    /**
     * Initialise le formulaire avec des valeurs
     */
    function setInitialValue(value: Partial<OwnerOutput>): void {
        if (value.isConnectedUser) {
            ownerType.value = "self"
            setOwnerFromCurrentUser()
            return
        }
        if (value.user) {
            user.value = {
                _id: value.user._id,
                firstname: value.user.firstname || "",
                lastname: value.user.lastname || "",
                username: value.user.username || "",
            }

            if (value.user._id) {
                foundUser.value = {
                    _id: value.user._id,
                    firstname: value.user.firstname,
                    lastname: value.user.lastname,
                    username: value.user.username,
                }
                hasSearchedUser.value = true
            }
        }

        if (value.company) {
            userType.value = "professional"

            if (value.company._id) {
                selectedCompany.value = {
                    _id: value.company._id,
                    name: value.company.name,
                    address: value.company.address || undefined,
                    type: value.company.type,
                    idcompany: value.company.idcompany,
                    legalForm: value.company.legalForm,
                }
            } else {
                setCompanyFormData({
                    name: value.company.name,
                    address: value.company.address,
                    type: value.company.type,
                    idcompany: value.company.idcompany || "",
                    legalForm: value.company.legalForm || "",
                })
            }
        }
    }

    // =====================
    // Watchers
    // =====================

    /**
     * Réinitialise le formulaire au changement de type (Mon entreprise ↔ Client externe)
     */
    watch(ownerType, (newType) => {
        if (newType === "self") {
            resetFormState()
            setOwnerFromCurrentUser()
        } else {
            reset()
        }
    })

    // =====================
    // Initialisation
    // =====================

    if (options.initialValue) {
        setInitialValue(options.initialValue)
    }

    return {
        // État
        ownerType,
        user,
        userType,
        selectedCompany,
        foundUser,
        hasSearchedUser,
        isSearchingByEmail,
        filteredSuggestions,
        suggestions,
        isSearchingSuggestions,

        // Formulaire entreprise (exposé pour le composant CompanyForm)
        companyForm,
        companyTypeOptions,
        legalFormOptions,
        companyIdErrorMessage,

        // Options
        getOwnerTypeOptions,
        getUserTypeOptions,

        // Computed
        getUserHasCompanies,
        getUserCompanies,
        getIsUserBasicValid,
        getIsUserValid,
        getIsCompanyValid,
        getIsValid,
        getShowCompanyForm,
        getShowUserTypeSelector,
        getAllowedUserTypeOptions,
        getUserTypeMismatch,
        getOutput,

        // Validation
        isValidEmail,
        isValidCompany,

        // Actions
        searchUser,
        setOwnerFromCurrentUser,
        selectCompany,
        filterSuggestions,
        onSelectSuggestion,
        resetSearchState,
        reset,
        setInitialValue,
        onCompanyTypeChange,
    }
}
